import React, { useEffect, useState } from 'react';

import { approverTooltipContent } from '../user/approverTooltip';
import UserApprovalListBudgetChangesApprovalsCompactRow from '../user/budget_approvals/UserApprovalListBudgetChangesApprovalsCompactRow';
import UserApprovalListExpenseFundApprovalsRow from '../user/UserApprovalListExpenseFundApprovalsRow';
import UserApprovalListPositionFundApprovalsRow from '../user/UserApprovalListPositionFundApprovalsRow';
import UserApprovalListPurchaseOrdersRow from '../user/UserApprovalListPurchaseOrdersRow';
import UserApprovalListPurchaseRequisitionsRow from '../user/UserApprovalListPurchaseRequisitionsRow';

import ApprovalDetailModal from './approvals/ApprovalDetailModal';
import { HomePanelCard } from './HomeSection';
import QueueTable from './myviews/QueueTable';
import {
    STATUS_PENDING,
    TYPE_BUDGET_CHANGE,
    TYPE_EXPENSE_FUND,
    TYPE_POSITION_FUND,
    TYPE_PURCHASE_ORDER,
    TYPE_PURCHASE_REQUISITION,
    waitingLabel,
} from './myviews/queueTypes';
import useHomeQueue from './myviews/useHomeQueue';

import useModal from 'Hooks/useModal';

import ArrowLeftIcon from 'Assets/icons/arrow-left.svg';
import ArrowRightIcon from 'Assets/icons/arrow-right.svg';
import CircleCheckIcon from 'Assets/icons/circle-check.svg';

/** The same ten-row cap every other Home list is served at. */
const PANEL_ROWS = 10;

const isRtl = () => typeof document !== 'undefined' && document.documentElement.dir === 'rtl';

/**
 * The approvals case of the one detail panel: the ten approvals waiting longest
 * on this user.
 *
 * THE SAME CARD AND THE SAME CAP AS EVERY OTHER LIST. Approvals are fetched
 * from the queue endpoint rather than the section endpoint and are drawn by the
 * register's own row components, so the data path is its own — but on the page
 * it is one of the lists a tile or an attention card opens, and it reads as one.
 *
 * A ROW OPENS THE SAME POP-UP THE REGISTER OPENS, so an approver can clear an
 * item without first changing tab. It takes its DOM id as a prop, so this
 * pop-up and the register's can be mounted at once — which they are, once the
 * user has visited both tabs.
 *
 * NO FULL-ROW WASH ON A LATE APPROVAL. How long each item has waited is already
 * the first column, in words, and the attention strip above leads with the
 * oldest of them. A red row behind that said the same thing a third time and
 * left the table unreadable on a queue where most items are late.
 *
 * "View all" goes to the register, which is the only surface that filters and
 * pages. `pendingTotal` is the TRUE total off the entitlement payload, not this
 * list's ten — an approver holding forty is told forty.
 */
export default function HomeApprovalsPanel({ approvalsQueueUrl, pendingTotal, onViewAll, onQueueChange, refreshToken, myViews = {} }) {
    /* PINNED TO PENDING, which is also what makes the row key below safe: the
       pending predicate narrows to `active = true`, and a chain activates one
       attempt at a time, so a record contributes exactly one row. Any status
       filter added here brings back the history buckets, where one record can
       hold several of this user's decisions and `id` alone stops identifying a
       row — use queueRowKey() then, as the register does. */
    const queue = useHomeQueue(approvalsQueueUrl, { perPage: PANEL_ROWS, initialStatus: STATUS_PENDING });

    /* The register acted. Both can be mounted at once, so this one re-reads
       rather than going on showing a row that is no longer waiting. Skipped on
       mount: the token starts at 0 and the queue has just loaded. */
    useEffect(() => {
        if (refreshToken) {
            queue.refresh();
        }
    }, [refreshToken]);

    const detailModal = useModal();
    const [activeRow, setActiveRow] = useState(null);

    useEffect(() => {
        activeRow ? detailModal.show() : detailModal.hide();
    }, [activeRow]);

    /* An action here empties a row out of the list, so the list and the
       entitlement payload behind the tiles are re-read together. */
    const afterAction = () => {
        setActiveRow(null);
        queue.refresh();
        onQueueChange?.();
    };

    const ArrowIcon = isRtl() ? ArrowLeftIcon : ArrowRightIcon;

    const tooltipContent = approverTooltipContent(myViews.display_approval_statuses);

    const columns = [
        { key: 'waiting', label: $t('Waiting'), className: 'tw-w-24' },
        { key: 'type', label: $t('Type'), className: 'tw-w-44' },
        { key: 'title', label: $t('Title') },
        { key: 'value', label: $t('Value'), className: 'tw-w-32' },
        { key: 'actions', label: $t('Actions'), className: 'tw-w-48' },
    ];

    const renderRow = (row) => {
        const shared = {
            key: `${row.type}:${row.id}`,
            row: row.record,
            typeLabel: row.type_label,
            waitingLabel: waitingLabel(row.sort_at),
            hideStatus: true,
            commonApprovalStatuses: myViews.common_approval_statuses,
            tooltipContent,
            /* The panel offers the same inline action the register does; the
               server still decides whether it is allowed. */
            canBeToggled: row.can_act_inline,
            isPending: () => true,
            onClickHandler: () => setActiveRow(row),
            toggle: () => setActiveRow(row),
        };

        switch (row.type) {
            case TYPE_PURCHASE_REQUISITION:
                return <UserApprovalListPurchaseRequisitionsRow {...shared} />;
            case TYPE_PURCHASE_ORDER:
                return <UserApprovalListPurchaseOrdersRow {...shared} />;
            case TYPE_POSITION_FUND:
                return <UserApprovalListPositionFundApprovalsRow {...shared} />;
            case TYPE_EXPENSE_FUND:
                return <UserApprovalListExpenseFundApprovalsRow {...shared} />;
            case TYPE_BUDGET_CHANGE:
                return <UserApprovalListBudgetChangesApprovalsCompactRow {...shared} href={row.href} />;
            default:
                return null;
        }
    };

    const total = typeof pendingTotal === 'number' ? pendingTotal : queue.rows.length;

    const footer = (
        <>
            <span>{$t('Showing {{shown}} of {{total}}', { shown: queue.rows.length, total })}</span>
            <button
                type="button"
                onClick={onViewAll}
                className="tw-inline-flex tw-items-center tw-gap-x-1 tw-border-0 tw-bg-transparent tw-p-0 tw-text-xs tw-font-semibold tw-text-blue"
            >
                {$t('View all in {{module}}', { module: $t('My approvals') })}
                <ArrowIcon className="tw-h-3.5 tw-w-3.5" aria-hidden="true" />
            </button>
        </>
    );

    return (
        <HomePanelCard title={$t('Awaiting your approval')} summary={$t('Longest waiting first')} footer={footer}>
            <QueueTable
                columns={columns}
                rows={queue.rows}
                renderRow={renderRow}
                loading={queue.loading}
                error={queue.error}
                /* NO PAGER on a capped list — the rows shown are the page, so
                   `total` never exceeds `perPage` and QueueTable draws none. The
                   figure that matters is in the footer, where it carries the
                   whole queue rather than this slice of it. */
                total={queue.rows.length}
                lastPage={1}
                page={1}
                perPage={PANEL_ROWS}
                onPageChange={onViewAll}
                emptyState={
                    <div className="tw-flex tw-flex-col tw-items-center tw-gap-2 tw-py-6 tw-text-center">
                        <CircleCheckIcon className="tw-h-6 tw-w-6 tw-text-green" aria-hidden="true" />
                        <p className="tw-text-sm tw-font-semibold tw-text-charcoal">{$t('All clear')}</p>
                        <p className="tw-text-xs tw-text-charcoal-60">{$t('Nothing is waiting on your approval right now.')}</p>
                    </div>
                }
            />

            <ApprovalDetailModal
                id="home-approval-detail-panel"
                modal={detailModal}
                row={activeRow}
                myViews={myViews}
                onClose={() => setActiveRow(null)}
                onActioned={afterAction}
            />
        </HomePanelCard>
    );
}
